import {
    Bell,
    Compass,
    Heart,
    Megaphone,
    Music,
    Settings,
    ShieldCheck,
    UserCircle,
    Users,
    type LucideIcon,
} from 'lucide-react';
import type { UserResponse } from '@/utils/types';

/**
 * Single source of truth for in-app navigation. The side nav and the header menu both
 * render from this, so labels, icons, routes and order cannot drift apart.
 */
export interface NavItem {
    id: 'discover' | 'jamboard' | 'collaborations' | 'liked' | 'notifications' | 'profile' | 'settings' | 'admin' | 'partner'
    label: string
    to: string
    Icon: LucideIcon
    strokeWidth?: number
    /** Extra paths where this item counts as the current page (e.g. the index route). */
    alsoActiveAt?: readonly string[]
    /** Role-gated destination. Menus draw these apart from everyday navigation. */
    restricted?: boolean
}

type NavUser = Pick<UserResponse, 'username' | 'isAdmin' | 'isPartner'> | null | undefined

/** Where people go to find music, projects and collaborators. Same order in every menu. */
export const BROWSE_NAV_ITEMS: readonly NavItem[] = [
    { id: 'discover', label: 'Discover', to: '/discover', Icon: Compass, alsoActiveAt: ['/'] },
    { id: 'jamboard', label: 'JamBoard', to: '/jamboard', Icon: Music, strokeWidth: 2.5 },
    { id: 'collaborations', label: 'Collaborations', to: '/collaborations', Icon: Users },
    { id: 'liked', label: 'Liked', to: '/liked', Icon: Heart },
    { id: 'notifications', label: 'Notifications', to: '/notifications', Icon: Bell },
];

export const SETTINGS_NAV_ITEM: NavItem = {
    id: 'settings',
    label: 'Settings',
    to: '/settings',
    Icon: Settings,
};

export const getProfileNavItem = (user: NavUser): NavItem => ({
    id: 'profile',
    label: 'Profile',
    to: user?.username ? `/${user.username}` : '/profile',
    Icon: UserCircle,
});

const ADMIN_NAV_ITEM: NavItem = {
    id: 'admin',
    label: 'Admin Dashboard',
    to: '/admin',
    Icon: ShieldCheck,
    restricted: true,
};

const PARTNER_NAV_ITEM: NavItem = {
    id: 'partner',
    label: 'Partner Dashboard',
    to: '/partner',
    Icon: Megaphone,
    restricted: true,
};

/** Admin and partner dashboards, only for users who hold those roles. */
export const getRoleNavItems = (user: NavUser): NavItem[] => [
    ...(user?.isAdmin === true ? [ADMIN_NAV_ITEM] : []),
    ...(user?.isPartner === true ? [PARTNER_NAV_ITEM] : []),
];

const normalizePath = (path: string) => path.toLowerCase();

/** True on the item's route, anything nested under it, or one of its aliases. */
export const isNavItemActive = (pathname: string, { to, alsoActiveAt = [] }: Pick<NavItem, 'to' | 'alsoActiveAt'>): boolean => {
    const current = normalizePath(pathname);
    const target = normalizePath(to);
    return current === target || current.startsWith(`${target}/`) || alsoActiveAt.includes(pathname);
};
